import { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, channelApi, errText } from "@/lib/api";

const formatPrice = (amount, currency) => new Intl.NumberFormat(undefined, {
  style: "currency",
  currency,
}).format(amount);

function StripePaymentForm({ amount, currency, paymentIntentId, returnUrl, onError, onComplete }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    onError("");
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      if (result.error) {
        onError(result.error.message || "Your payment could not be completed. Check your details and try again.");
        return;
      }
      const { data } = await channelApi.post("/customer/tips/confirm-payment-intent", { payment_intent_id: paymentIntentId });
      if (data.status === "succeeded") onComplete(data);
      else onError("Your payment is still processing. This page will update once Stripe confirms it.");
    } catch (error) {
      onError(errText(error, "We couldn't confirm your payment yet. Please retry shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-5" data-testid="channel-stripe-payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <button type="submit" disabled={!stripe || !elements || busy} className="flex w-full items-center justify-center gap-2 bg-[#E62314] px-4 py-3.5 text-sm font-bold text-white hover:bg-[#C51B10] disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/45" data-testid="channel-checkout-pay">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Processing payment…" : `Pay ${formatPrice(amount, currency)}`}
      </button>
    </form>
  );
}

export default function ChannelCheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const showId = params.get("show_id");
  const returnedPaymentIntentId = params.get("payment_intent");
  const [customer, setCustomer] = useState(null);
  const [show, setShow] = useState(null);
  const [amount, setAmount] = useState("1.00");
  const [minimum, setMinimum] = useState(100);
  const [currency, setCurrency] = useState("USD");
  const [precision, setPrecision] = useState(2);
  const [loading, setLoading] = useState(true);
  const [startingPayment, setStartingPayment] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [complete, setComplete] = useState(false);
  const [receipt, setReceipt] = useState({ status: "", email: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([api.get("/channel"), channelApi.get("/customer/account")])
      .then(([channelResponse, accountResponse]) => {
        if (!active) return;
        const channel = channelResponse.data;
        const account = accountResponse.data;
        const accountPrecision = account.currency_precision ?? 2;
        const floor = account.minimum_tip_minor ?? (10 ** accountPrecision);
        const amountStep = 10 ** accountPrecision;
        const selectedShow = showId ? channel.on_demand?.find((item) => item.id === showId) : null;
        setMinimum(floor);
        setCurrency(account.currency || "USD");
        setPrecision(accountPrecision);
        setAmount((floor / amountStep).toFixed(accountPrecision));
        setShow(selectedShow);
        setCustomer(account);
        if (!account.tip_currency_supported) setError("Tips aren’t available in your account currency yet.");
        if (showId && !selectedShow) setError("That title is no longer available for a tip.");
      })
      .catch((requestError) => {
        if (active) setError(errText(requestError, "We couldn't load your checkout. Please try again."));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [showId]);

  useEffect(() => {
    if (!returnedPaymentIntentId) return undefined;
    let active = true;
    channelApi.post("/customer/tips/confirm-payment-intent", { payment_intent_id: returnedPaymentIntentId })
      .then(({ data }) => {
        if (!active) return;
        if (data.status === "succeeded") {
          setReceipt({ status: data.receipt_email_status || "unknown", email: data.receipt_email || "" });
          setComplete(true);
          setError("");
        } else {
          setError("Your payment is still processing. It will appear in your payment history once confirmed.");
        }
        const nextUrl = showId ? `/channel/checkout?show_id=${encodeURIComponent(showId)}` : "/channel/checkout";
        window.history.replaceState({}, "", nextUrl);
      })
      .catch((requestError) => { if (active) setError(errText(requestError, "We couldn't confirm your payment yet. Please try again.")); });
    return () => { active = false; };
  }, [returnedPaymentIntentId, showId]);

  const amountScale = 10 ** precision;
  const minimumAmount = minimum / amountScale;
  const validAmount = Number.isFinite(Number(amount)) && Math.round(Number(amount) * amountScale) >= minimum;
  const completePayment = (data) => {
    setReceipt({ status: data.receipt_email_status || "unknown", email: data.receipt_email || "" });
    setComplete(true);
  };
  const beginPayment = async () => {
    setStartingPayment(true);
    setError("");
    try {
      const { data } = await channelApi.post("/customer/tips/payment-intent", {
        amount_minor: Math.round(Number(amount) * amountScale),
        show_id: showId || undefined,
      });
      if (!data.client_secret || !data.publishable_key || !data.payment_intent_id) {
        throw new Error("Stripe did not initialize this payment. Please try again.");
      }
      setPaymentIntentId(data.payment_intent_id);
      setClientSecret(data.client_secret);
      setStripePromise(loadStripe(data.publishable_key));
    } catch (requestError) {
      setError(errText(requestError, "We couldn't connect to secure payment. Please try again."));
    } finally {
      setStartingPayment(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white" data-testid="channel-checkout-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
          <a href="/channel" className="group flex items-center gap-3">
            <img src="/assets/logo.png" alt="Cresway Media logo" className="h-12 w-12 object-contain transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110" />
            <span className="font-display text-lg font-extrabold">Cresway Channel</span>
          </a>
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-white/65 hover:text-white" data-testid="channel-checkout-back"><ArrowLeft className="h-4 w-4" />Back</button>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <p className="flex items-center gap-2 font-mono-label text-xs uppercase tracking-[0.2em] text-[#FF8B7F]"><Heart className="h-4 w-4" />Channel support</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold">Your tip</h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-white/60">Choose an amount to support Cresway Channel. Watching remains free.</p>

        {loading ? <div className="mt-10 flex items-center gap-3 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" />Loading checkout…</div> : complete ? (
          <section className="mt-10 max-w-xl border-y border-white/15 py-8" role="status" data-testid="channel-checkout-success">
            <p className="font-mono-label text-xs uppercase tracking-[0.18em] text-[#FF8B7F]">Payment confirmed</p>
            <h2 className="mt-3 font-display text-2xl font-extrabold">Thank you for your tip.</h2>
            <p className="mt-2 text-sm text-white/60">Your payment is confirmed and will appear in your account history.</p>
            {receipt.status === "sent" ? <p className="mt-3 text-sm text-white/80" data-testid="channel-receipt-email-sent">A receipt has been emailed to <strong className="text-white">{receipt.email || customer?.email}</strong>.</p>
              : receipt.status === "failed" ? <p className="mt-3 text-sm text-[#FF8B7F]" data-testid="channel-receipt-email-failed">Your payment is confirmed, but we couldn’t email the receipt{receipt.email || customer?.email ? ` to ${receipt.email || customer?.email}` : ""}. Please contact support.</p>
                : receipt.status === "sending" ? <p className="mt-3 text-sm text-white/70" data-testid="channel-receipt-email-sending">Your receipt is being emailed{receipt.email || customer?.email ? ` to ${receipt.email || customer?.email}` : ""}.</p>
                  : null}
            <button type="button" onClick={() => navigate("/channel")} className="mt-6 inline-flex items-center gap-2 bg-[#E62314] px-4 py-3 text-sm font-bold hover:bg-[#C51B10]" data-testid="channel-checkout-done">Return to Cresway Channel</button>
          </section>
        ) : (
          <div className="mt-9 grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <section className="space-y-6" aria-label="Tip details">
              <div className="border-y border-white/15 py-5">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Supporting</p>
                <p className="mt-2 font-display text-xl font-bold">{show?.title || "Cresway Channel"}</p>
                {customer && <p className="mt-2 text-sm text-white/55">Receipt for {customer.email}</p>}
              </div>
              <label className="block text-sm font-semibold" htmlFor="channel-tip-amount">Tip amount
                <span className="ml-2 text-white/50">in {currency}; minimum {formatPrice(minimumAmount, currency)}</span>
                <div className="mt-2 flex items-center border border-white/25 focus-within:border-[#FF4A36]">
                  <input id="channel-tip-amount" type="number" min={minimumAmount} step={1 / amountScale} value={amount} disabled={!!clientSecret} onChange={(event) => setAmount(event.target.value)} className="w-full bg-transparent px-4 py-3.5 outline-none disabled:text-white/60" data-testid="channel-checkout-amount" />
                </div>
              </label>
              {clientSecret && stripePromise && (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "night", variables: { colorPrimary: "#E62314", colorBackground: "#111111", colorText: "#FFFFFF" } } }}>
                  <StripePaymentForm amount={Number(amount) || 0} currency={currency} paymentIntentId={paymentIntentId} returnUrl={window.location.href} onError={setError} onComplete={completePayment} />
                </Elements>
              )}
              {error && <p className="border-l-2 border-[#FF4A36] pl-3 text-sm text-[#FF8B7F]" role="alert">{error}</p>}
            </section>

            <aside className="border-t border-white/15 pt-5 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0" aria-label="Payment summary">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Summary</p>
              <div className="mt-5 flex items-center justify-between gap-4 border-b border-white/10 pb-4 text-sm"><span>{show?.title || "Channel tip"}</span><strong>{formatPrice(Number(amount) || 0, currency)}</strong></div>
              <div className="flex items-center justify-between py-4 text-sm font-bold"><span>Total</span><span>{formatPrice(Number(amount) || 0, currency)}</span></div>
              <p className="border-l-2 border-white/20 pl-3 text-sm leading-6 text-white/55">Card details are collected securely by Stripe and never pass through Cresway Channel.</p>
              {!clientSecret && <button type="button" onClick={beginPayment} disabled={!validAmount || !!error || startingPayment || !customer} className="mt-6 flex w-full items-center justify-center gap-2 bg-[#E62314] px-4 py-3.5 text-sm font-bold text-white hover:bg-[#C51B10] disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/45" data-testid="channel-checkout-submit">
                {startingPayment && <Loader2 className="h-4 w-4 animate-spin" />}{startingPayment ? "Preparing secure payment…" : `Continue to payment · ${formatPrice(Number(amount) || 0, currency)}`}
              </button>}
              {!customer && <a href="/channel" className="mt-4 inline-block text-sm font-semibold text-[#FF8B7F] underline underline-offset-4">Sign in to continue</a>}
              <a href="/channel/privacy" className="mt-5 inline-block text-xs text-white/50 underline underline-offset-4 hover:text-white">Privacy Policy</a>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}